// Single source of truth for customer-facing RMA status display.
// Both RmaTimelineCard and OrgDevicesPage must use this — never duplicate this logic.

export const ACTION_BLUE = '#3b82f6';
export const ACTION_BLUE_BG = 'rgba(59,130,246,0.08)';
export const ACTION_BLUE_BORDER = 'rgba(59,130,246,0.45)';
export const WAITING_GREEN = '#46bb89';
export const WAITING_GREEN_BG = 'rgba(70,187,137,0.06)';
export const WAITING_GREEN_BORDER = 'rgba(70,187,137,0.35)';

export const RMA_STATUS_LABELS = {
  pending: 'Request received',
  initiated: 'Request received',
  assigned: 'Ship your device',
  received: 'Device received',
  awaiting_repair: 'Awaiting assessment',
  repairing: 'In service',
  awaiting_shipping: 'Ready to return',
  shipped: 'On its way back',
  closed_case: 'Case closed',
  archived: 'Case closed & archived',
  closed: 'Case closed & archived',
  canceled_by_customer: 'Cancelled'
};

export const RMA_STATUS_COLORS = {
  pending: '#f59e0b',
  initiated: '#f59e0b',
  assigned: ACTION_BLUE,
  received: '#a78bfa',
  awaiting_repair: '#a78bfa',
  repairing: '#fb923c',
  awaiting_shipping: ACTION_BLUE,
  shipped: WAITING_GREEN,
  closed_case: '#9ca3af',
  archived: '#9ca3af',
  closed: '#9ca3af',
  canceled_by_customer: '#9ca3af'
};

/**
 * Returns true when the customer has a pending quote decision (warranty denied, approval needed).
 */
function hasPendingQuoteDecision(rma) {
  const approval = rma?.repair?.customerApproval;
  if (!approval || approval.required !== true) return false;
  const s = approval.status || 'pending';
  return s === 'pending' || s === 'challenged';
}

/**
 * Returns the computed display state for a customer-facing RMA card.
 *
 * Blue  = customer must take action (ship device, approve quote, confirm delivery)
 * Green = waiting on North Invent
 */
export function getRmaCustomerDisplayState(rma) {
  const status = rma?.status || 'pending';
  const hasSubmittedTracking = Boolean(rma?.customerShipment?.trackingNumber);

  const hasConfirmedReturnAddress = Boolean(rma?.shippingAddress?.recipientName);

  // All cases where the customer needs to take action
  const isActionRequired =
    (status === 'assigned' && !hasSubmittedTracking) ||
    (status === 'repairing' && hasPendingQuoteDecision(rma)) ||
    (status === 'awaiting_shipping' && !hasConfirmedReturnAddress);

  // Cases where NI is actively working and nothing is needed from the customer
  const isWaitingOnNI =
    (status === 'assigned' && hasSubmittedTracking) ||
    (status === 'repairing' && !hasPendingQuoteDecision(rma)) ||
    (status === 'awaiting_shipping' && hasConfirmedReturnAddress);

  let statusLabel;
  if (isWaitingOnNI && status === 'assigned') {
    statusLabel = 'Waiting on North Invent';
  } else if (status === 'repairing' && hasPendingQuoteDecision(rma)) {
    statusLabel = 'Decision required';
  } else {
    statusLabel = RMA_STATUS_LABELS[status] || status;
  }

  const statusColor = isWaitingOnNI
    ? WAITING_GREEN
    : isActionRequired
    ? ACTION_BLUE
    : RMA_STATUS_COLORS[status] || '#9ca3af';

  const badgeText = isActionRequired
    ? 'Action required'
    : isWaitingOnNI
    ? 'Waiting on North Invent'
    : null;

  const badgeColor = isActionRequired ? ACTION_BLUE : WAITING_GREEN;
  const badgeBg = isActionRequired ? ACTION_BLUE_BG : WAITING_GREEN_BG;
  const badgeBorder = isActionRequired ? ACTION_BLUE_BORDER : WAITING_GREEN_BORDER;

  const cardBg = isActionRequired
    ? ACTION_BLUE_BG
    : isWaitingOnNI
    ? WAITING_GREEN_BG
    : 'rgba(255,255,255,0.04)';

  const cardBorder = isActionRequired
    ? `1px solid ${ACTION_BLUE_BORDER}`
    : isWaitingOnNI
    ? `1px solid ${WAITING_GREEN_BORDER}`
    : '1px solid rgba(255,255,255,0.08)';

  return {
    isActionRequired,
    isWaitingOnNI,
    statusLabel,
    statusColor,
    badgeText,
    badgeColor,
    badgeBg,
    badgeBorder,
    cardBg,
    cardBorder
  };
}

/** Returns the count of RMAs that still require customer action. */
export function countActionRequired(rmas) {
  if (!Array.isArray(rmas)) return 0;
  return rmas.filter(r => getRmaCustomerDisplayState(r).isActionRequired).length;
}
